import { css } from 'aphrodite';
import moment from 'moment-timezone';
import { parse as parseQueryStringParams } from 'query-string';
import * as React from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import {
	invalidateSurveyByToken,
	useClientGetEventRegistrationResponseByToken,
	useClientGetStripeCheckoutSession,
} from '../../../../../../queries';
import defaultImage from '../../../../../../web/assets/default_event_image.svg';
import { LoadingLogo } from '../../../../../../web/components/LoadingLogo';
import { IEventRegistrationSurvey, IEventSurveyResponse } from '../../../../../models/Api';
import { EventSurveyConfirmation } from './EventSurveyConfirmation';
import { EventSurveyForm } from './EventSurveyForm';
import { styleSheet } from './styles';

interface IProps {
	survey: IEventRegistrationSurvey;
}

const FALL_BACK_IMAGE = defaultImage;

export enum EventSurveyPaymentStatus {
	PaymentStarted = 'PaymentStarted',
	PaymentPending = 'PaymentPending',
	PaymentSucceeded = 'PaymentSucceeded',
	PaymentFailed = 'PaymentFailed',
}

/**
 * EventSurvey Component - Handles event registration with payment flow
 *
 * TOKEN LIFECYCLE FOR PAID EVENTS:
 *
 * Initial State:
 * - User receives OLD_TOKEN in email/URL
 * - Page loads with OLD_TOKEN in route params
 *
 * After Payment:
 * 1. Stripe redirects back with sessionId in URL query params
 * 2. Component parses sessionId synchronously in useState initialization (line 50)
 * 3. useClientGetStripeCheckoutSession queries with OLD_TOKEN to get NEW_TOKEN
 * 4. Backend allows OLD_TOKEN for checkout session lookup (special permission)
 * 5. Effect at line 103 detects token change and redirects to NEW_TOKEN URL
 * 6. After redirect, byToken query fetches complete response with NEW_TOKEN
 *
 * Query Coordination:
 * - byToken query is DISABLED when stripeSessionId exists (line 77)
 * - This prevents race condition where byToken might fire with OLD_TOKEN
 * - After token change redirect, byToken re-enables with NEW_TOKEN
 */
export const EventSurvey = ({ survey }: IProps) => {
	// Capture the match here to pass down to child
	const match = useRouteMatch<{
		token?: string;
	}>();

	const { params: urlParams = {}, path: matchPath } = match;
	const h = useHistory<{ response?: IEventSurveyResponse; search?: { checkoutStarted?: boolean } }>();

	/**
	 * Parse sessionId SYNCHRONOUSLY from URL before any queries run
	 * This prevents race condition where useClientGetEventRegistrationResponseByToken
	 * fires with old token before useEffect can disable it
	 *
	 * Use useState with initialization function to ensure it only runs once
	 * and doesn't re-read from history.location.search after URL is cleaned
	 *
	 * SessionId can also be set later when payment completes via callback
	 */
	const [stripeSessionId, setStripeSessionId] = React.useState<string | null>(() => {
		const queryObject = parseQueryStringParams(h.location.search);
		if (queryObject.sessionId && typeof queryObject.sessionId === 'string') {
			return queryObject.sessionId;
		}
		return null;
	});

	const [loading, setLoading] = React.useState<boolean>(true);
	const [editResponse, setEditResponse] = React.useState<boolean>(false);
	const [isProcessingStripePayment, setIsProcessingStripePayment] = React.useState<boolean>(false);
	const responseRef = React.useRef<IEventSurveyResponse | null>(null);

	// Query checkout session after payment to get NEW_TOKEN (backend returns just the token string)
	const { data: newTokenFromCheckout, isLoading: isLoadingCheckoutSession } = useClientGetStripeCheckoutSession({
		sessionId: stripeSessionId,
		refetchOnWindowFocus: false,
		token: urlParams.token,
	});

	const { data: recordedResponse } = useClientGetEventRegistrationResponseByToken({
		accessToken: urlParams.token,
		retry: false,
		refetchOnWindowFocus: false,
		gcTime: 0,
		// Disable this query when we're fetching via checkout session OR processing Stripe payment
		// After payment, we get the response from checkout session endpoint - NO POLLING
		enabled: !stripeSessionId && !isProcessingStripePayment,
	});

	/**
	 * Clean URL after sessionId is parsed
	 * Remove sessionId from query params for cleaner URL
	 */
	React.useEffect(() => {
		if (stripeSessionId) {
			h.replace({
				pathname: h.location.pathname,
				search: '',
			});
		}
	}, [stripeSessionId, h]);

	/**
	 * Handle token change after payment (backend generates new token in webhook)
	 *
	 * When checkout session returns the new token:
	 * 1. Backend webhook created response with NEW_TOKEN
	 * 2. We queried checkout session with OLD_TOKEN (which is allowed)
	 * 3. Backend returned just the NEW_TOKEN as a string
	 * 4. This effect detects the new token and redirects to it
	 * 5. After redirect, the byToken query will fetch the full response with the NEW_TOKEN
	 */
	React.useEffect(() => {
		if (newTokenFromCheckout && newTokenFromCheckout !== urlParams.token) {
			// Clear stripeSessionId before redirect so the byToken query can run with new token
			setStripeSessionId(null);

			const newPath = matchPath.replace(':token?', encodeURIComponent(newTokenFromCheckout));

			h.replace({
				pathname: newPath,
			});
		}
	}, [newTokenFromCheckout, urlParams.token, h, matchPath, setStripeSessionId]);

	/**
	 * Use recordedResponse from byToken query
	 * After payment redirect with NEW_TOKEN, this will fetch the complete response
	 */
	const activeResponse = recordedResponse;

	/**
	 * When a event survey link is sent via lev email additional metadata about the user is injected
	 * a partial event survey response is recorded including name, email and phone number but isAttending response is not set
	 * in this case we want to show the user the RSVP form even though a surveyResponse is present
	 */
	const isPartialSurveyResponseFromEmail = activeResponse?.isAttending === undefined;

	const successfulPayments = activeResponse?.payments?.filter(p => p.status === 'PaymentSucceeded');

	const showConfirmationMessage = React.useMemo(() => {
		if (survey?.isPaidEvent && !editResponse && !successfulPayments?.length && activeResponse?.isAttending) {
			return false;
		}

		return !!(survey && !editResponse && !isPartialSurveyResponseFromEmail && activeResponse?.id);
	}, [
		successfulPayments,
		survey,
		editResponse,
		activeResponse?.isAttending,
		activeResponse?.id,
		isPartialSurveyResponseFromEmail,
	]);

	const onResponseSubmitted = (response: IEventSurveyResponse) => {
		if (editResponse && !survey?.isPaidEvent) {
			setEditResponse(false);
			setLoading(true);
		}
		responseRef.current = response;
		if (response.token && response.token !== urlParams.token) {
			h.replace({
				pathname: matchPath.replace(':token?', encodeURIComponent(response.token)),
				state: {
					response,
				},
			});
		}
	};

	const onPaymentStarted = React.useCallback(() => {
		setIsProcessingStripePayment(true);
	}, []);

	const onPaymentCompleted = React.useCallback(
		(sessionId: string) => {
			setIsProcessingStripePayment(false);
			setStripeSessionId(sessionId);
			// No polling needed - checkout session query will provide the response
		},
		[setStripeSessionId]
	);

	const switchToEditResponse = () => {
		invalidateSurveyByToken();
		setEditResponse(true);
	};

	const startMoment = moment.tz(survey?.eventInformation?.startTime, survey?.eventInformation?.timeZone);
	const endMoment = survey?.eventInformation?.endTime
		? moment.tz(survey?.eventInformation?.endTime, survey?.eventInformation?.timeZone)
		: null;
	const formattedEndDate = endMoment
		? startMoment.isSame(endMoment, 'day')
			? ` - ${endMoment.format('hh:mm a')}`
			: ` - ${endMoment.format('MMM Do, YYYY, hh:mm a')}`
		: '';
	const timezoneAbbr = moment().tz(survey?.eventInformation?.timeZone)?.format('z');
	const formattedEventDate = `${startMoment.format('MMM Do, YYYY, hh:mm a')}${formattedEndDate} (${timezoneAbbr})`;
	// Legacy: simple ticket count (only for costPerAttendee events)
	const formattedAmount = React.useMemo(() => {
		if (survey?.attendeeOptions?.costPerAttendee) {
			const dollarAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(survey?.attendeeOptions?.costPerAttendee / 100);
			return dollarAmount;
		}
		return 0;
	}, [survey?.attendeeOptions?.costPerAttendee]);

	return (
		<div className={css(styleSheet.container)}>
			{/* Processing overlay - shown while waiting for checkout session response after payment */}
			{stripeSessionId && isLoadingCheckoutSession && (
				<section
					className={css(styleSheet.processingOverlay)}
					role='dialog'
					aria-modal='true'
					aria-labelledby='processing-title'
				>
					<LoadingLogo size={80} />
					<div>
						<h2 id='processing-title' className={css(styleSheet.processingMessage)}>
							Processing your payment...
						</h2>
						<p className={css(styleSheet.processingMessage)} aria-live='polite'>
							Please do not close or refresh this window.
						</p>
					</div>
				</section>
			)}

			<div className={css(styleSheet.eventDetailsContainer)}>
				<figure className={css(styleSheet.previewImageContainer)}>
					<img className={css(styleSheet.previewImage)} src={survey?.eventInformation?.image?.url || FALL_BACK_IMAGE} />
				</figure>
				<section className={css(styleSheet.details)}>
					<div>
						<h4 className={css(styleSheet.eventDetailsTitle)}>When</h4>
						<p>{formattedEventDate}</p>
					</div>
					<div>
						<h4 className={css(styleSheet.eventDetailsTitle)}>Where</h4>
						<p className={css(styleSheet.locationAreaWrap)}>{survey?.eventInformation?.location}</p>
					</div>
					<div>
						<h4 className={css(styleSheet.eventDetailsTitle)}>Details</h4>
						<pre className={css(styleSheet.detailsAreaWrap)}>{survey?.eventInformation?.details}</pre>
					</div>
					{/* Legacy: simple ticket count (only for costPerAttendee events) */}
					{survey?.isPaidEvent && survey?.attendeeOptions?.costPerAttendee ? (
						<div>
							<h4 className={css(styleSheet.eventDetailsTitle)}>Cost</h4>
							<p className={css(styleSheet.eventPrice)}>{formattedAmount}/ticket</p>
						</div>
					) : null}
				</section>
			</div>

			{showConfirmationMessage ? (
				<EventSurveyConfirmation
					survey={survey}
					surveyResponse={responseRef.current || activeResponse}
					onChangeResponseClick={switchToEditResponse}
				/>
			) : (
				<EventSurveyForm
					survey={survey}
					surveyResponse={activeResponse}
					onResponseSubmitted={onResponseSubmitted}
					routeMatch={match} // Pass the route match as a prop
					loading={loading}
					setLoading={setLoading}
					cancelEdit={() => setEditResponse(false)}
					isEdit={editResponse}
					onPaymentStarted={onPaymentStarted}
					onPaymentCompleted={onPaymentCompleted}
					isAttending={responseRef?.current?.isAttending}
				/>
			)}
		</div>
	);
};
